import {
  ReceiptText,
  Landmark,
  Globe,
  Smartphone,
  Megaphone,
  TrendingUp,
  PenTool,
  Briefcase,
  UserCheck,
  Cpu,
  Award,
  Target,
  MessagesSquare,
  Infinity as InfinityIcon,
  Instagram,
} from "lucide-react";

export const SOCIALS = [
  {
    label: "Instagram",
    handle: "@faithhbusinessconsultancy",
    href: "https://www.instagram.com/faithhbusinessconsultancy",
    icon: Instagram,
  },
];

export const CONTACT = {
  whatsapp: "971522242844",
  whatsappDisplay: "+971 52 224 2844",
  phone: "+971557795417",
  phoneDisplay: "+971 55 779 5417",
  email: "faithhbusinessconsultancy@gmail.com",
  location: "United Arab Emirates",
};

export const whatsappLink = (message) =>
  `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(message)}`;

export const SERVICE_OPTIONS = [
  "VAT Filing",
  "Corporate Tax Filing",
  "Website Development",
  "App Development",
  "Digital Marketing",
  "Advertising",
  "Logo & Branding",
  "Other",
];

export const SERVICES = [
  {
    id: "vat-filing",
    icon: ReceiptText,
    title: "VAT Filing",
    tagline: "Accurate, on-time UAE VAT return preparation and filing — fully aligned with FTA requirements.",
    benefits: ["FTA-compliant returns", "Deadline tracking", "Penalty avoidance"],
    featured: true,
  },
  {
    id: "corporate-tax",
    icon: Landmark,
    title: "Corporate Tax Filing",
    tagline: "Corporate tax registration, computation and return filing handled end-to-end for your business.",
    benefits: ["Registration support", "Accurate computation", "Compliance calendar"],
    featured: true,
  },
  {
    id: "website-development",
    icon: Globe,
    title: "Website Development",
    tagline: "Modern, responsive, conversion-focused websites that make your business look established.",
    benefits: ["Mobile-first design", "SEO-friendly build", "Fast & secure"],
  },
  {
    id: "app-development",
    icon: Smartphone,
    title: "App Development",
    tagline: "Custom mobile applications built around your business workflows and customer experience.",
    benefits: ["iOS & Android", "Tailored UX/UI", "Scalable architecture"],
  },
  {
    id: "advertising",
    icon: Megaphone,
    title: "Advertising",
    tagline: "High-impact ad campaigns across Google, Meta and social platforms that reach the right audience.",
    benefits: ["Targeted campaigns", "Creative production", "Budget optimisation"],
  },
  {
    id: "digital-marketing",
    icon: TrendingUp,
    title: "Digital Marketing",
    tagline: "Social media, content and digital strategies that turn attention into enquiries and customers.",
    benefits: ["Social media management", "SEO & content", "Performance reporting"],
  },
  {
    id: "logo-branding",
    icon: PenTool,
    title: "Logo Design & Branding",
    tagline: "Distinctive logos and complete brand identities that make your business unforgettable.",
    benefits: ["Custom logo design", "Brand guidelines", "Full identity kits"],
  },
  {
    id: "business-support",
    icon: Briefcase,
    title: "Business Support",
    tagline: "Practical consultancy and day-to-day business support so you can focus on running your company.",
    benefits: ["Business advisory", "Documentation help", "Ongoing support"],
  },
];

export const PILLARS = [
  {
    number: "01",
    title: "Compliance",
    text: "VAT and corporate tax handled properly, on time, every time — so your business stays protected and penalty-free under UAE regulations.",
  },
  {
    number: "02",
    title: "Digital Presence",
    text: "Websites, apps and branding that make your business look as professional online as it is in real life.",
  },
  {
    number: "03",
    title: "Growth",
    text: "Marketing and advertising strategies that bring your business in front of more customers — and turn visibility into revenue.",
  },
];

export const STANDARDS = [
  { value: 8, suffix: "", label: "Core services under one roof" },
  { value: 3, suffix: "-step", label: "From first call to final launch" },
  { value: 1, suffix: "", label: "Dedicated partner for your business" },
  { value: 100, suffix: "%", label: "Transparent communication" },
];

export const DEV_FEATURES = [
  "Custom-built websites and applications — never generic templates",
  "Fully responsive across mobile, tablet and desktop",
  "Modern technology stacks with fast load times",
  "SEO-friendly structure so customers can find you",
  "Built to convert visitors into enquiries",
];

export const MARKETING_FEATURES = [
  "Social media strategy and day-to-day management",
  "Paid advertising across Meta, Google and social platforms",
  "Scroll-stopping creative — visuals, copy and campaigns",
  "SEO and content that build long-term visibility",
  "Clear reporting so you always know what's working",
];

export const TAX_POINTS_VAT = [
  "VAT return preparation & filing",
  "FTA registration & de-registration",
  "Filing deadline tracking & reminders",
  "Record-keeping guidance",
];

export const TAX_POINTS_CT = [
  "Corporate tax registration",
  "Taxable income computation",
  "Annual return filing",
  "Compliance risk review",
];

export const WHY_ITEMS = [
  { icon: UserCheck, title: "Personalized Solutions", text: "No copy-paste packages. Every solution is shaped around your business, your goals and your budget." },
  { icon: Cpu, title: "Modern Technology", text: "We build with current, proven technology — fast, secure and ready to scale as you grow." },
  { icon: Award, title: "Professional Service", text: "A dedicated, detail-obsessed team that treats your business like our own reputation depends on it. Because it does." },
  { icon: Target, title: "Business-Focused", text: "Everything we deliver is judged by one standard: does it help your business grow?" },
  { icon: MessagesSquare, title: "Transparent Communication", text: "Clear pricing, honest timelines and straight answers. You'll always know exactly where things stand." },
  { icon: InfinityIcon, title: "End-to-End Support", text: "From first consultation to launch and beyond — one partner for compliance, digital and growth." },
];

export const PROCESS_STEPS = [
  {
    number: "01",
    title: "Tell Us Your Business",
    text: "A free, no-pressure consultation. We listen, understand your goals and recommend exactly what you need — nothing more.",
  },
  {
    number: "02",
    title: "We Build Your Solution",
    text: "Our team gets to work — filings, websites, apps, branding or campaigns — with clear updates at every stage.",
  },
  {
    number: "03",
    title: "Grow Your Business",
    text: "Launch, measure, refine. We stay by your side as your business grows from compliance to real momentum.",
  },
];

export const FAQS = [
  {
    q: "Does my business need to register for corporate tax in the UAE?",
    a: "Most businesses operating in the UAE are required to register for corporate tax with the FTA, including many free zone companies. The specifics depend on your activity and structure. We'll assess your situation in a free consultation and handle the registration and filing for you.",
  },
  {
    q: "When are VAT returns due in the UAE?",
    a: "VAT returns are typically due quarterly (monthly for some larger businesses), within 28 days of the end of your tax period. Late filing attracts FTA penalties. We track your deadlines and file on time, every time.",
  },
  {
    q: "How long does it take to build a website?",
    a: "A professional business website typically takes 1–2 weeks depending on scope, content and features. Custom web applications and mobile apps are scoped individually. You'll get a clear timeline before we start — and we stick to it.",
  },
  {
    q: "Do you work with startups and small businesses?",
    a: "Absolutely. Faithh was built for businesses of every size — from brand-new startups needing their first logo and VAT registration, to established companies that need a full digital presence and growth strategy.",
  },
  {
    q: "What do you need from me to get started?",
    a: "Just a conversation. Tell us about your business and what you want to achieve — via the form below, WhatsApp or a call. We'll take it from there and tell you exactly what we need, step by step.",
  },
  {
    q: "How much do your services cost?",
    a: "Pricing depends on the service and scope — a VAT filing is very different from a custom app. What we promise: transparent, quoted-up-front pricing with no hidden costs. Request a free consultation for a tailored quote.",
  },
];

// To add real work: copy the Bright Future entry shape — image in /public/assets, live link, blurb.
export const PORTFOLIO_ITEMS = [
  {
    id: "work-brightfuture",
    title: "Bright Future Textile",
    category: "Website Development",
    image: "/assets/work-brightfuture.jpg",
    link: "https://brightfuturetextile.com",
    live: true,
    blurb: "Premium kandura fabrics & tailoring, Al Ain — complete website design & development.",
  },
  { id: "work-apps", title: "Mobile Applications", category: "App Development", icon: Smartphone },
  { id: "work-more", title: "Your Project Here", category: "Let's Build It", icon: Briefcase },
];

export const MARQUEE_ITEMS = [
  "VAT Filing",
  "Corporate Tax",
  "Website Development",
  "App Development",
  "Digital Marketing",
  "Advertising",
  "Logo & Branding",
  "Business Support",
];

export const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Development", href: "#development" },
  { label: "Marketing", href: "#marketing" },
  { label: "Compliance", href: "#compliance" },
  { label: "Our Work", href: "#work" },
  { label: "FAQ", href: "#faq" },
];
